import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import {
  CheckoutOrchestrator,
  shouldGateCheckout,
  type CheckoutApiConfig,
} from "@baluu/ui/components/checkout";
import { fetchExistingParticipants } from "@baluu/ui/lib/checkout";
import { CHECKOUT_FRAME_CLASSES } from "@baluu/ui/lib/checkout/frame";
import { buildBookingOptions } from "@baluu/ui/lib/checkout/build-booking-options";
import { useShadowPortal } from "~/widgets/lib/use-shadow-portal";
import { useAuthSnapshot } from "~/widgets/lib/auth-client";
import { describeErrorForLog, formatCurrency } from "~/widgets/lib/utils";
import { TermsDialogPortal } from "../../../TermsDialogPortal";
import { CustomerAuthDialog } from "../../../CustomerAuthDialog/CustomerAuthDialog";
import { bookingJson, messageOf } from "../../api";
import { Notice } from "../../parts";
import type { BundleCourse, BundleDoc, ManageSchool } from "./types";
import { itemFromCheckout, type CheckoutItemData, type RedemptionItem } from "./redemption";
import { RedeemConfirm, useCheckoutConfig, type CheckoutConfig } from "./RedeemConfirm";

// Booking with the bundle, in an overlay of its own (outside the page's
// shadow root, so it's fixed to the viewport whatever the host page does).
// From the Services tab: how to book the listing (v1's BookingOptionsPopup,
// bundles left out), then the shared checkout in bundle mode (v1's
// CheckoutOverlay with initialBundleId), then the confirmation. From the
// Timetable tab: straight to the confirmation.

export type RedeemRequest =
  | { kind: "course"; course: BundleCourse }
  | { kind: "items"; items: RedemptionItem[]; spots: number; title: string };

export type PlacedOrder = { id: string; title: string; spots: number };

type BuildArgs = Parameters<typeof buildBookingOptions>[0];
type OptionType = Parameters<BuildArgs["onSelect"]>[0];
type CourseWithBundles = { course?: BuildArgs["course"]; ticketTypes?: BuildArgs["ticketTypes"]; ticketTypesV2?: unknown[] };

export function RedeemOverlay({
  api,
  school,
  bundle,
  request,
  primaryColor,
  termsHref,
  onClose,
  onPlaced,
}: {
  api: string;
  school: ManageSchool;
  bundle: BundleDoc;
  request: RedeemRequest;
  primaryColor?: string;
  termsHref: string;
  onClose: () => void;
  onPlaced: (order: PlacedOrder) => void;
}) {
  const mount = useShadowPortal({ open: true, id: "baluu-bundle-redeem", primaryColor });
  const [signInOpen, setSignInOpen] = useState(false);
  const [terms, setTerms] = useState<{ open: boolean; config?: CheckoutConfig; ticketPolicies?: unknown[] }>({ open: false });
  const [type, setType] = useState<OptionType | null>(null);
  const [checkedOut, setCheckedOut] = useState<RedemptionItem | null>(null);

  const course = request.kind === "course" ? request.course : null;
  const options = useQuery({
    queryKey: ["booking", "bundle-course-options", api, course?._id],
    enabled: !!course,
    queryFn: () => bookingJson<CourseWithBundles>(`${api}/widget/courses/${encodeURIComponent(course!._id)}/with-bundles`),
  });
  const optionList =
    options.data?.course != null
      ? buildBookingOptions({
          course: options.data.course,
          bundles: [],
          ticketTypes: options.data.ticketTypes ?? [],
          ticketTypesV2: options.data.ticketTypesV2 ?? [],
          dismissBundles: true,
          onSelect: () => undefined,
        }).filter((o) => o.type !== "bundle")
      : [];
  // One way to book: no choice to make.
  const chosen = type ?? (optionList.length === 1 ? optionList[0].type : null);

  const currency = school.preferredCurrency ?? "GBP";
  const placed = (orderId: string, spots: number, title: string) => onPlaced({ id: orderId, title, spots });

  const dialogs = (
    <>
      <TermsDialogPortal
        isOpen={terms.open}
        onClose={() => setTerms((t) => ({ ...t, open: false }))}
        settings={terms.config?.termsSettings}
        customTerms={terms.config?.customTerms ?? undefined}
        timezone={terms.config?.timezone ?? school.timezone}
        primaryColor={primaryColor}
        ticketPolicies={terms.ticketPolicies as Parameters<typeof TermsDialogPortal>[0]["ticketPolicies"]}
      />
      <CustomerAuthDialog
        open={signInOpen}
        onOpenChange={setSignInOpen}
        apiUrl={api}
        businessSlug={school.slug}
        schoolName={school.name}
        primaryColor={primaryColor}
        onSignedIn={() => setSignInOpen(false)}
      />
    </>
  );

  let title = "Book with your bundle";
  let body: ReactNode;
  if (request.kind === "items") {
    title = "Confirm your booking";
    body = (
      <RedeemConfirm
        api={api}
        school={school}
        bundle={bundle}
        items={request.items}
        spots={request.spots}
        termsHref={termsHref}
        onSignIn={() => setSignInOpen(true)}
        onOpenTerms={(config) => setTerms({ open: true, config })}
        onPlaced={(id) => placed(id, request.spots, request.title)}
      />
    );
  } else if (checkedOut) {
    const spots = checkedOut.ticketSelections.reduce((s, t) => s + (t.quantity || 0), 0);
    title = "Confirm your booking";
    body = (
      <RedeemConfirm
        api={api}
        school={school}
        bundle={bundle}
        items={[checkedOut]}
        spots={spots}
        termsHref={termsHref}
        onSignIn={() => setSignInOpen(true)}
        onOpenTerms={(config) => setTerms({ open: true, config })}
        onPlaced={(id) => placed(id, spots, checkedOut.courseName || request.course.title || "Booking")}
        onBack={() => setCheckedOut(null)}
      />
    );
  } else if (chosen) {
    // The shared checkout takes the whole overlay, as it does everywhere.
    return mount
      ? createPortal(
          <div className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50">
            <BundleCheckout
              api={api}
              schoolId={school._id}
              listingId={request.course._id}
              bookingType={chosen}
              bundleId={bundle.id}
              primaryColor={primaryColor}
              onClose={onClose}
              onDone={(data) => setCheckedOut(itemFromCheckout(data, { schoolId: school._id, bundleId: bundle.id, currency }))}
              onOpenSignIn={() => setSignInOpen(true)}
              onOpenTerms={(data) => setTerms({ open: true, config: { termsSettings: data.termsSettings, customTerms: data.customTerms }, ticketPolicies: data.ticketPolicies })}
            />
            {dialogs}
          </div>,
          mount,
        )
      : null;
  } else if (options.isLoading) {
    body = (
      <div className="flex justify-center py-12" aria-busy="true">
        <Spinner className="h-6 w-6" />
      </div>
    );
  } else if (options.isError) {
    body = (
      <Notice tone="error">
        {messageOf(options.error, "We couldn’t load how to book this. Please try again.")}{" "}
        <button type="button" className="font-medium underline" onClick={() => options.refetch()}>
          Try again
        </button>
      </Notice>
    );
  } else if (optionList.length === 0) {
    body = <Notice tone="info">There’s nothing to book on this listing right now. Please check back later.</Notice>;
  } else {
    title = request.course.title || title;
    body = (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Choose how you’d like to book. Your bundle covers the price.</p>
        <ul className="space-y-2">
          {optionList.map((option) => (
            <li key={`${option.type}-${option.title}`}>
              <button
                type="button"
                onClick={() => setType(option.type)}
                className="flex w-full items-center justify-between gap-4 rounded-lg border p-4 text-left hover:bg-muted/50"
              >
                <span>
                  <span className="block font-medium">{option.title}</span>
                  {option.description && <span className="block text-sm text-muted-foreground">{option.description}</span>}
                </span>
                {typeof option.price === "number" && (
                  <span className="shrink-0 text-sm text-muted-foreground line-through" aria-label="Covered by your bundle">
                    {formatCurrency(option.price, currency)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (!mount) return null;
  return createPortal(
    <div
      className="checkout-overlay-content flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !signInOpen && !terms.open) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="bundle-redeem-title" className="my-8 w-full max-w-2xl rounded-xl bg-background p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id="bundle-redeem-title" className="text-lg font-semibold">
            {title}
          </h2>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" aria-hidden />
          </Button>
        </div>
        {body}
      </div>
      {dialogs}
    </div>,
    mount,
  );
}

/** The shared checkout in bundle mode, for one listing and way of booking
 * (CheckoutFlow's set-up, ending at the confirmation rather than the
 * basket). */
function BundleCheckout({
  api,
  schoolId,
  listingId,
  bookingType,
  bundleId,
  primaryColor,
  onClose,
  onDone,
  onOpenSignIn,
  onOpenTerms,
}: {
  api: string;
  schoolId: string;
  listingId: string;
  bookingType: OptionType;
  bundleId: string;
  primaryColor?: string;
  onClose: () => void;
  onDone: (data: CheckoutItemData) => void;
  onOpenSignIn: () => void;
  onOpenTerms: (data: { termsSettings?: CheckoutConfig["termsSettings"]; customTerms?: CheckoutConfig["customTerms"]; ticketPolicies?: unknown[] }) => void;
}) {
  const auth = useAuthSnapshot();
  const isLoggedIn = auth.status === "authenticated";
  const apiConfig: CheckoutApiConfig = {
    baseUrl: api,
    source: "widget",
    isWebcomponent: true,
    ...(isLoggedIn && auth.user?.email ? { buyerEmail: auth.user.email } : {}),
    onError: (error, context) => {
      // Message and status only: the error can carry the customer's bearer.
      console.error("[webcomponents] Bundle checkout error:", describeErrorForLog(error), context);
    },
  };
  const config = useCheckoutConfig(api, schoolId);
  const participants = useQuery({
    queryKey: ["booking", "bundle-participants", api, schoolId, auth.token],
    enabled: isLoggedIn && !!schoolId,
    queryFn: () => fetchExistingParticipants(schoolId, auth.token, apiConfig),
  });
  const requireLogin = !!config.data?.requireLoginToBook;
  const gated = shouldGateCheckout({ requireLogin, isLoggedIn, onLogin: onOpenSignIn });

  if (config.isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Spinner className="h-8 w-8 text-white" />
      </div>
    );
  }

  return (
    <CheckoutOrchestrator
      listingId={listingId}
      apiConfig={apiConfig}
      // No primaryColor: the host's variables colour it (see CheckoutFlow).
      onClose={onClose}
      onSuccess={onClose}
      onError={(error) => console.error("[webcomponents] Bundle checkout error:", describeErrorForLog(error))}
      allowInlineReceipt
      closeOverlayOnPanelDismiss
      renderHeader={({ activeCheckout, onClose: close }) =>
        activeCheckout && !gated ? null : (
          <header className={`sticky top-0 z-50 border-b ${CHECKOUT_FRAME_CLASSES.header}`}>
            <div className="container mx-auto flex items-center justify-end px-1 py-3 sm:px-4">
              <button onClick={close} className={`rounded-full p-2 transition-colors ${CHECKOUT_FRAME_CLASSES.close}`} aria-label="Close">
                <X className="h-6 w-6" />
              </button>
            </div>
          </header>
        )
      }
      initialBookingType={bookingType}
      initialBundleId={bundleId}
      isDirectBooking
      directCheckout
      onProceedToBasketCheckout={(data) => onDone(data as CheckoutItemData)}
      onOpenTermsDialog={(data) => onOpenTerms(data)}
      isLoggedIn={isLoggedIn}
      onLogin={onOpenSignIn}
      requireLogin={requireLogin}
      existingParticipants={participants.data ?? []}
    />
  );
}
